"use client";

import { clsx, Slot } from "@trydig/ui";
import { useIntersectionObserverAPI } from "@trydig/ui/hooks";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import type { ProductDetails } from "@/lib/centra";
import { AddToCartButton } from "../AddToCartButton";
import { ProductPrice } from "../ProductPrice";
import { VariantSelector } from "../VariantSelector";
import styles from "./StickyATC.module.css";

export interface StickyATCProps {
	product: ProductDetails;
}

const stickyATCAtom = atom<boolean>(false);

const footerVisibleAtom = atom<boolean>(false);

export const StickyATCSentinel = () => {
	const [_, setDisplay] = useAtom(stickyATCAtom);

	const obserrver = useIntersectionObserverAPI(([entry]) => {
		setDisplay(!entry.isIntersecting);
	});

	return (
		<span
			style={{
				position: "absolute",
				width: "1px",
				height: "1px",
				overflow: "hidden",
				zIndex: 1000,
			}}
			ref={(element) => {
				obserrver?.observe(element!);
				return () => {
					obserrver?.disconnect();
				};
			}}
		/>
	);
};

export const FooterVisibility = ({ children }: React.PropsWithChildren) => {
	const setFooterVisible = useSetAtom(footerVisibleAtom);
	const obserrver = useIntersectionObserverAPI(
		([entry]) => {
			setFooterVisible(entry.isIntersecting);
		},
		{
			threshold: 0.75,
		},
	);
	return (
		<Slot
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			ref={(element: any) => {
				obserrver?.observe(element!);
				return () => {
					obserrver?.disconnect();
				};
			}}
		>
			{children}
		</Slot>
	);
};

export const StickyATC = ({ product }: StickyATCProps) => {
	const detailsOutOfView = useAtomValue(stickyATCAtom);
	const footerVisible = useAtomValue(footerVisibleAtom);

	const shouldDisplay = detailsOutOfView && !footerVisible;

	if (!product) return null;

	return (
		<div className={clsx(styles.root, shouldDisplay && styles.visible)}>
			<div>
				<span className={styles.name}>{product.name}</span>
				<ProductPrice
					discountedPrice={product.price ?? ""}
					price={product.priceBeforeDiscount ?? ""}
					className={styles.price}
				/>
			</div>
			<div className={styles.variants}>
				{product.variants.length > 0 && (
					<VariantSelector
						hideHeader
						variants={[
							{
								...product,
								selected: true,
							},
							...(product.variants as ProductDetails[]),
						]}
					/>
				)}
				<AddToCartButton product={product} />
			</div>
		</div>
	);
};
